import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HStack, Box } from '@cad-arc';
import { BoloPanelFormType, EventPanelConfiguration, EventPanelOptions } from '@mark43/cad-api';
import { FormInputButton } from '../../../forms/integrated-form-components/form-button';
import { getDefaultPerson } from '../../../event-panel/event-panel-form/involved-person-vehicle-fields/persons/helpers';
import {
    EntityPanelFormGroup,
    EntityPanelGroupHeader,
} from '../../../panels/entity-panels/entity-panel-components';
import { NoMembersIndicator } from '../../../event-panel/tabs/components/no-members-indicator';
import { ConditionalWrapper } from '../../../common/components/conditional-wrapper';
import { TabCard } from '../../../event-panel/tabs/components/tab-card';
import { useTrackedFieldArray } from '../../../forms/use-tracked-field-array';
import { BoloPanelFormIsPrimaryCheck } from './bolo-panel-form-is-primary-check';
import { EntityPanelFormPersonFieldset } from '../../../panels/entity-panels/forms/persons/entity-panel-person-fields';

export type BoloPanelFormPersonsFieldsProps = {
    form: UseFormReturn<BoloPanelFormType>;
    allowMembersControl?: boolean;
    noMembersText?: string;
    showTitle?: boolean;
    displayAsCards?: boolean;
    eventPanelOptions: EventPanelOptions;
    eventPanelConfig?: EventPanelConfiguration;
    isReadOnly?: boolean;
    isClearedBolo?: boolean;
};

export function BoloPanelFormPersonsFields(
    props: BoloPanelFormPersonsFieldsProps
): React.ReactElement {
    const boloForm = props.form;
    const { fields, append, remove } = useTrackedFieldArray({
        control: boloForm.control,
        name: 'involvedPersons',
        keyName: 'rhfKey',
    });

    const addPersonButton =
        props.allowMembersControl && !props.isClearedBolo ? (
            <FormInputButton
                size="md"
                variant="ghost"
                leadingVisual="Add"
                data-testid="add-involved-person-button"
                onClick={() => append(getDefaultPerson(props.eventPanelConfig))}
            >
                Add Involved Person
            </FormInputButton>
        ) : undefined;

    return (
        <EntityPanelFormGroup data-testid="involved-persons-form-section">
            {fields.length !== 0 && props.showTitle ? (
                <EntityPanelGroupHeader title="Involved Persons" />
            ) : undefined}

            {props.noMembersText && fields.length === 0 ? (
                <NoMembersIndicator
                    text={props.noMembersText}
                    icon="Person"
                    value={fields}
                    action={addPersonButton}
                />
            ) : (
                <>
                    {fields.map((field, index) => {
                        return (
                            <ConditionalWrapper
                                key={field.rhfKey}
                                condition={props.displayAsCards}
                                wrapper={(children) => <TabCard>{children}</TabCard>}
                            >
                                <>
                                    <HStack>
                                        <BoloPanelFormIsPrimaryCheck
                                            form={props.form}
                                            index={index}
                                            basePath="involvedPersons"
                                        />
                                    </HStack>
                                    <EntityPanelFormPersonFieldset
                                        entityPanelOptions={props.eventPanelOptions}
                                        baseKey="involvedPersons"
                                        index={index}
                                        remove={remove}
                                        displayAsCards={props.displayAsCards}
                                        isReadOnly={props.isReadOnly}
                                        hideQueryInDex
                                        allowMembersControl={index !== 0}
                                    />
                                </>
                            </ConditionalWrapper>
                        );
                    })}
                    <Box>{addPersonButton}</Box>
                </>
            )}
        </EntityPanelFormGroup>
    );
}
